import { supabase } from "@/integrations/supabase/client";
import { getGuestPreviewId } from "@/lib/guest-preview";
import { getStripeEnvironment } from "@/lib/stripe";

export type Msg = { role: "user" | "assistant"; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;

export interface LimitReachedInfo {
  limit_reached: true;
  tier: string;
  limit: number;
  used: number;
}

export async function streamChat({
  messages,
  ageGroup,
  subject,
  onDelta,
  onDone,
  onError,
  onLimitReached,
}: {
  messages: Msg[];
  ageGroup: string;
  subject: string;
  onDelta: (text: string) => void;
  onDone: () => void;
  onError: (error: string) => void;
  onLimitReached?: (info: LimitReachedInfo) => void;
}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const guestId = session?.user ? null : getGuestPreviewId();

  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      messages,
      ageGroup,
      subject,
      guestId,
      environment: getStripeEnvironment(),
    }),
  });

  const contentType = resp.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    const data = await resp.json().catch(() => ({ error: "Something went wrong" }));

    if (data.limit_reached && onLimitReached) {
      onLimitReached(data as LimitReachedInfo);
      return;
    }

    if (!resp.ok || data.error) {
      onError(data.error || "Something went wrong. Please try again!");
      return;
    }
  }

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({ error: "Something went wrong" }));
    if (data.limit_reached && onLimitReached) {
      onLimitReached(data as LimitReachedInfo);
      return;
    }
    onError(data.error || "Something went wrong. Please try again!");
    return;
  }

  if (!resp.body) {
    onError("No response received");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let textBuffer = "";
  let streamDone = false;
  let streamError: string | null = null;

  const processEvent = (jsonStr: string) => {
    const parsed = JSON.parse(jsonStr);
    if (parsed.type === "error" || parsed.type === "response.failed") {
      streamError = parsed.error?.message || parsed.response?.error?.message || "Buddy couldn't finish that reply. Please try again.";
      return;
    }
    const content = (parsed.choices?.[0]?.delta?.content ?? (parsed.type === "response.output_text.delta" ? parsed.delta : undefined)) as string | undefined;
    if (content) onDelta(content);
  };

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    textBuffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
      let line = textBuffer.slice(0, newlineIndex);
      textBuffer = textBuffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;

      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      try {
        processEvent(jsonStr);
      } catch {
        textBuffer = line + "\n" + textBuffer;
        break;
      }
    }
  }

  if (textBuffer.trim()) {
    for (let raw of textBuffer.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (raw.startsWith(":") || raw.trim() === "") continue;
      if (!raw.startsWith("data: ")) continue;
      const jsonStr = raw.slice(6).trim();
      if (jsonStr === "[DONE]") continue;
      try {
        processEvent(jsonStr);
      } catch {
        // Ignore malformed tail chunks.
      }
    }
  }

  if (streamError) {
    onError(streamError);
    return;
  }

  onDone();
}
